import InfiniteMarquee from "@/components/InfiniteMarquee";

const steps = [
    {
        title: "Discovery & strategy",
        body: "Map the work, find where AI helps, and write the scope. The fee is fixed before anyone builds.",
    },
    {
        title: "Build & test",
        body: "Prototype against real data. You get a weekly update, not a reveal at the end.",
    },
    {
        title: "Deploy & monitor",
        body: "Watch cost and performance after launch. Privacy and consent are checked before it goes live.",
    },
    {
        title: "Scale & optimize",
        body: "Tune what shipped, add the next feature, and stay on support.",
    },
];

const stack = [
    { label: "AI & ML", items: ["Anthropic Claude", "OpenAI GPT-4o", "Google Gemini"] },
    { label: "Frontend", items: ["React", "Next.js", "TypeScript", "Tailwind CSS", "Vite"] },
    { label: "Backend", items: ["Supabase", "PostgreSQL", "Auth", "Edge Functions", "Next.js", "Stripe"] },
    { label: "Infrastructure", items: ["Vercel", "GitHub Actions", "Sentry"] },
];

export default function ProcessBand() {
    return (
        <section id="process" aria-labelledby="process-heading" className="bg-white px-5 pb-6 sm:pb-10">
            <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[28px] bg-[#0a0a0f] text-white shadow-[0_28px_70px_rgba(10,10,15,0.16)]">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(520px 220px at 12% 0%, rgba(43,80,255,0.38), transparent 62%), radial-gradient(420px 200px at 92% 18%, rgba(99,132,255,0.16), transparent 60%)",
                    }}
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-[0.18]"
                    style={{
                        backgroundImage:
                            "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
                        backgroundSize: "72px 72px",
                        maskImage: "linear-gradient(180deg, #000 0%, transparent 72%)",
                    }}
                />

                <div className="relative px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#9eb0ff]">Our process</p>
                            <h2 id="process-heading" className="mt-2 font-display text-3xl font-extrabold leading-tight sm:text-4xl">
                                From strategy to production
                            </h2>
                        </div>
                        <p className="max-w-sm text-sm leading-relaxed text-white/60">
                            Four phases. One owner from the first map through what ships, and what keeps running after.
                        </p>
                    </div>

                    <div className="relative mt-8">
                        <ol className="process-steps grid gap-3 md:grid-cols-4 md:gap-4">
                            {steps.map((step, i) => (
                                <li
                                    key={step.title}
                                    style={{ "--i": i }}
                                    className="process-card relative overflow-hidden rounded-2xl bg-white/[0.045] p-4 ring-1 ring-white/10 backdrop-blur-sm sm:p-5"
                                >
                                    <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 z-20 h-[3px] bg-white/10">
                                        <span className="process-seg block h-full w-full bg-gradient-to-r from-[#2b50ff] to-[#d7e0ff]" />
                                    </span>
                                    <div className="flex items-center gap-3 md:block">
                                        <span className="process-num relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 font-display text-sm font-extrabold ring-1 ring-white/15">
                                            {i + 1}
                                        </span>
                                        <h3 className="font-display text-base font-bold leading-snug md:mt-4">{step.title}</h3>
                                    </div>
                                    <p className="mt-2 text-sm leading-relaxed text-white/65">{step.body}</p>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <div className="mt-8 border-t border-white/10 pt-6">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/45">
                            Tools our products run on
                        </p>
                        <div
                            className="process-stack relative mt-4"
                            style={{
                                maskImage: "linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)",
                            }}
                        >
                            <InfiniteMarquee
                                duration={42}
                                copies={2}
                                groupClassName="gap-2.5 pr-2.5"
                                trackClassName="process-track"
                            >
                                {stack.map((group) => (
                                    <span key={group.label} className="flex items-center gap-2.5">
                                        <span className="rounded-full bg-[#2b50ff] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                                            {group.label}
                                        </span>
                                        {group.items.map((item) => (
                                            <span
                                                key={`${group.label}-${item}`}
                                                className="rounded-full bg-white/[0.06] px-3.5 py-1.5 text-sm text-white/80 ring-1 ring-white/10"
                                            >
                                                {item}
                                            </span>
                                        ))}
                                    </span>
                                ))}
                            </InfiniteMarquee>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
